import React, { useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { Printer, ArrowLeft, ZoomIn, ZoomOut } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { LOGOS, BRANCHES } from "@/lib/brand";

// Dimensions réelles de la bâche en millimètres
const BANNER_W = 3000;
const BANNER_H = 1500;

// Logo "tout-en-un" (brushstroke + EQUINOXE + GROUPE)
const GROUPE_LOGO =
  "https://customer-assets.emergentagent.com/job_enterprise-hub-357/artifacts/6sv95ykm_Equinoxe_Groupe_noir-resize.png";

function Banner() {
  return (
    <div
      data-testid="banner-canvas"
      className="banner-print relative bg-black text-white overflow-hidden"
      style={{ width: `${BANNER_W}mm`, height: `${BANNER_H}mm` }}
    >
      {/* Bandeau supérieur 3 couleurs */}
      <div className="absolute top-0 left-0 right-0 flex" style={{ height: "40mm" }}>
        <div className="flex-1" style={{ backgroundColor: "#8b5cf6" }} />
        <div className="flex-1" style={{ backgroundColor: "#ea580c" }} />
        <div className="flex-1" style={{ backgroundColor: "#0ea5e9" }} />
      </div>

      {/* Bandeau inférieur 3 couleurs */}
      <div className="absolute bottom-0 left-0 right-0 flex" style={{ height: "40mm" }}>
        <div className="flex-1" style={{ backgroundColor: "#8b5cf6" }} />
        <div className="flex-1" style={{ backgroundColor: "#ea580c" }} />
        <div className="flex-1" style={{ backgroundColor: "#0ea5e9" }} />
      </div>

      {/* Halo lumineux subtil */}
      <div
        className="absolute inset-0"
        style={{
          background:
            "radial-gradient(60% 50% at 50% 50%, rgba(255,255,255,0.06), transparent 70%)",
        }}
      />

      {/* Contenu principal */}
      <div
        className="relative h-full flex items-stretch"
        style={{ padding: "70mm 90mm" }}
      >
        {/* Colonne gauche : Logo principal */}
        <div
          className="flex items-center justify-center flex-shrink-0"
          style={{ width: "900mm" }}
        >
          <img
            src={GROUPE_LOGO}
            alt="Equinoxe Groupe"
            style={{ width: "880mm", height: "880mm", objectFit: "contain" }}
          />
        </div>

        {/* Colonne droite */}
        <div
          className="flex flex-col"
          style={{ flex: 1, paddingLeft: "90mm" }}
        >
          {/* Slogan */}
          <div>
            <div
              style={{
                fontSize: "26mm",
                letterSpacing: "0.3em",
                color: "#9ca3af",
                textTransform: "uppercase",
                fontWeight: 500,
              }}
            >
              Groupe Equinoxe
            </div>
            <h1
              style={{
                fontFamily: "'Space Grotesk', sans-serif",
                fontSize: "115mm",
                fontWeight: 700,
                letterSpacing: "-0.025em",
                lineHeight: 0.95,
                marginTop: "14mm",
              }}
            >
              Trois expertises.
              <br />
              <span style={{ color: "#9ca3af" }}>Une même exigence.</span>
            </h1>
          </div>

          {/* 3 pôles avec logos et activités */}
          <div
            className="flex items-stretch"
            style={{ gap: "30mm", marginTop: "50mm", flex: 1 }}
          >
            {["studio", "securite", "proprete"].map((key) => {
              const p = BRANCHES[key];
              const services = p.services.filter((s) => s !== "Autre");
              return (
                <div
                  key={key}
                  className="flex flex-col items-center text-center"
                  style={{
                    flex: 1,
                    borderTop: `12mm solid ${p.accent}`,
                    paddingTop: "22mm",
                  }}
                >
                  {/* Logo dans carré blanc */}
                  <div
                    className="rounded-md bg-white flex items-center justify-center"
                    style={{
                      width: "340mm",
                      height: "340mm",
                      marginBottom: "24mm",
                      boxShadow: "0 8mm 30mm -8mm rgba(0,0,0,0.6)",
                    }}
                  >
                    <img
                      src={LOGOS[key]}
                      alt={p.name}
                      style={{ width: "92%", height: "92%", objectFit: "contain" }}
                    />
                  </div>

                  {/* Nom du pôle */}
                  <div
                    style={{
                      fontFamily: "'Space Grotesk', sans-serif",
                      fontSize: "28mm",
                      fontWeight: 600,
                      color: p.accent,
                      lineHeight: 1,
                      letterSpacing: "-0.01em",
                    }}
                  >
                    Equinoxe
                  </div>
                  <div
                    style={{
                      fontFamily: "'Space Grotesk', sans-serif",
                      fontSize: "50mm",
                      fontWeight: 700,
                      letterSpacing: "-0.02em",
                      color: "#fff",
                      lineHeight: 1,
                      marginTop: "5mm",
                    }}
                  >
                    {p.short}
                  </div>

                  {/* Activités principales */}
                  <ul
                    style={{
                      listStyle: "none",
                      padding: 0,
                      margin: "20mm 0 0 0",
                      textAlign: "left",
                      alignSelf: "stretch",
                    }}
                  >
                    {services.map((svc) => (
                      <li
                        key={svc}
                        style={{
                          fontSize: "26mm",
                          lineHeight: 1.2,
                          color: "#e5e5e5",
                          marginBottom: "12mm",
                          paddingLeft: "18mm",
                          position: "relative",
                          fontWeight: 500,
                          letterSpacing: "0.02em",
                        }}
                      >
                        <span
                          style={{
                            position: "absolute",
                            left: 0,
                            top: "8mm",
                            width: "8mm",
                            height: "8mm",
                            backgroundColor: p.accent,
                            borderRadius: "1.5mm",
                          }}
                        />
                        {svc}
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>

          {/* Contact en bas */}
          <div className="flex items-end justify-between" style={{ marginTop: "40mm" }}>
            <div>
              <div
                style={{
                  fontSize: "18mm",
                  letterSpacing: "0.25em",
                  color: "#9ca3af",
                  textTransform: "uppercase",
                  fontWeight: 500,
                }}
              >
                Nous joindre
              </div>
              <div
                style={{
                  fontFamily: "'Space Grotesk', sans-serif",
                  fontSize: "85mm",
                  fontWeight: 700,
                  letterSpacing: "-0.02em",
                  marginTop: "8mm",
                  lineHeight: 0.95,
                }}
              >
                05&nbsp;32&nbsp;18&nbsp;90&nbsp;44
              </div>
              <div
                style={{
                  fontSize: "18mm",
                  color: "#22c55e",
                  fontWeight: 600,
                  marginTop: "8mm",
                  letterSpacing: "0.02em",
                }}
              >
                Aussi disponible sur WhatsApp
              </div>
            </div>
            <div style={{ textAlign: "right" }}>
              <div
                style={{
                  fontSize: "22mm",
                  letterSpacing: "0.25em",
                  color: "#9ca3af",
                  textTransform: "uppercase",
                  fontWeight: 600,
                  marginBottom: "10mm",
                }}
              >
                Devis sous 24h
              </div>
              <div
                style={{
                  fontFamily: "'Space Grotesk', sans-serif",
                  fontSize: "40mm",
                  fontWeight: 700,
                  letterSpacing: "0.04em",
                  color: "#fff",
                  textTransform: "uppercase",
                }}
              >
                groupeequinoxe.fr
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function BannerPage() {
  const { user, ready } = useAuth();
  const [zoom, setZoom] = useState(0.13);

  if (ready === false) return null;
  if (ready && !user) return <Navigate to="/admin/login" replace />;

  return (
    <div className="pt-28 pb-24 min-h-screen" data-testid="page-banner">
      <div className="max-w-[1320px] mx-auto px-6 lg:px-8">
        <Link
          to="/admin"
          className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-zinc-500 hover:text-white mb-6 print:hidden"
          data-testid="banner-back-admin"
        >
          <ArrowLeft size={14} /> Retour au tableau de bord
        </Link>

        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 print:hidden">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-zinc-500">Communication</div>
            <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight mt-2">
              Bâche façade — 3 × 1,5 m
            </h1>
            <p className="mt-3 text-zinc-400 text-sm max-w-2xl">
              Maquette dimensionnée pour une bâche de <strong>3000 × 1500 mm</strong>. L'aperçu
              ci-dessous est affiché à l'échelle, le fichier d'impression est à taille réelle.
            </p>
          </div>
          <div className="flex gap-3 items-center print:hidden">
            <button
              onClick={() => setZoom((z) => Math.max(0.08, z - 0.03))}
              className="p-2 rounded-md border border-white/15 text-zinc-300 hover:bg-white/5"
              data-testid="banner-zoom-out"
              aria-label="Réduire"
            >
              <ZoomOut size={16} />
            </button>
            <div className="text-xs text-zinc-500 w-12 text-center">{Math.round(zoom * 100)}%</div>
            <button
              onClick={() => setZoom((z) => Math.min(0.5, z + 0.03))}
              className="p-2 rounded-md border border-white/15 text-zinc-300 hover:bg-white/5"
              data-testid="banner-zoom-in"
              aria-label="Agrandir"
            >
              <ZoomIn size={16} />
            </button>
            <button
              onClick={() => window.print()}
              data-testid="print-banner"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-white text-black font-semibold hover:bg-zinc-200"
            >
              <Printer size={16} /> Imprimer / PDF
            </button>
          </div>
        </div>

        {/* Aperçu mis à l'échelle */}
        <div className="mt-12 overflow-auto rounded-xl border border-white/10 bg-[#0e0e0e] p-6 print:p-0 print:border-0 print:bg-transparent">
          <div
            style={{
              width: `${BANNER_W * zoom * 3.78}px`,
              height: `${BANNER_H * zoom * 3.78}px`,
              margin: "0 auto",
            }}
            className="print:hidden"
          >
            <div
              style={{
                width: `${BANNER_W}mm`,
                height: `${BANNER_H}mm`,
                transform: `scale(${zoom})`,
                transformOrigin: "top left",
              }}
            >
              <Banner />
            </div>
          </div>
          {/* Version pour l'impression (taille réelle) */}
          <div className="hidden print:block">
            <Banner />
          </div>
        </div>

        {/* Specs */}
        <div
          className="mt-10 rounded-xl border border-white/10 bg-[#0e0e0e] p-8 print:hidden"
          data-testid="banner-specs"
        >
          <div className="text-xs uppercase tracking-[0.25em] text-zinc-500 mb-4">
            Spécifications pour l'imprimeur
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 text-sm">
            <Spec label="Format final" value="3000 × 1500 mm" />
            <Spec label="Ratio" value="2:1 (paysage)" />
            <Spec label="Fond perdu" value="+25 mm sur tous les bords" />
            <Spec label="Œillets" value="Tous les 50 cm sur le pourtour" />
            <Spec label="Matériau suggéré" value="Bâche PVC 510 g/m²" />
            <Spec label="Finition" value="Soudures + ourlet renforcé" />
            <Spec label="Résolution" value="150 dpi (échelle 1:1)" />
            <Spec label="Quadrichromie" value="CMJN" />
            <Spec label="Couleurs accents" value="#8b5cf6 · #ea580c · #0ea5e9" />
            <Spec label="Polices" value="Space Grotesk · Manrope" />
            <Spec label="Lisibilité" value="Cible : ≥ 10 m de recul" />
            <Spec label="Pose" value="Façade extérieure" />
          </div>
          <div className="mt-6 text-xs text-zinc-500 leading-relaxed">
            <strong className="text-zinc-300">Conseil impression</strong> : pour une bâche
            extérieure, demandez à votre imprimeur un PVC 510 g/m² (résistant aux UV et au vent),
            avec ourlet renforcé + œillets aux 4 coins et tous les 50 cm sur le pourtour. Pour
            l'export du fichier, choisissez « Enregistrer au format PDF » dans la fenêtre
            d'impression, et activez « Graphiques d'arrière-plan ».
          </div>
        </div>
      </div>

      <style>{`
        @media print {
          @page { size: 3000mm 1500mm; margin: 0; }
          html, body { background: white !important; }
          nav, header, footer { display: none !important; }
          .banner-print { box-shadow: none !important; }
        }
      `}</style>
    </div>
  );
}

function Spec({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500">{label}</div>
      <div className="mt-1 text-white">{value}</div>
    </div>
  );
}
